/**
 * KpisView
 *
 * Headline KPIs grouped by audience (CFO / Payments / Ops / Growth).
 * Metrics that require Stripe Billing history (NRR, GRR, cohort retention)
 * are still Phase 2 and rendered as em-dashes with a note.
 */

import React, { Component } from 'react';
import { adminService } from '../../../_services/admin.service';

const fmtGBP = (n, opts = {}) => {
    if (n == null || isNaN(n)) return '—';
    const { decimals = 2 } = opts;
    return '£' + Number(n).toLocaleString('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};
const fmtInt = (n) => (n == null || isNaN(n)) ? '—' : Number(n).toLocaleString('en-GB');
const pct = (n, d = 1) => (n == null || isNaN(n)) ? '—' : `${(Number(n) * 100).toFixed(d)}%`;
const signedPct = (n, d = 1) => {
    if (n == null || isNaN(n)) return '—';
    const v = Number(n) * 100;
    return `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`;
};
const decimal = (n, d = 1) => (n == null || isNaN(n)) ? '—' : Number(n).toFixed(d);

const AUDIENCE_TABS = [
    { id: 'cfo', label: 'CFO / Financial' },
    { id: 'payments', label: 'Payments' },
    { id: 'ops', label: 'Ops health' },
    { id: 'growth', label: 'Growth' }
];

class KpisView extends Component {
    state = { loading: true, error: null, data: null, audience: 'cfo' };

    componentDidMount() { this.fetch(); }
    componentDidUpdate(prevProps) {
        if (prevProps.startDate !== this.props.startDate || prevProps.endDate !== this.props.endDate) {
            this.fetch();
        }
    }

    fetch = async () => {
        this.setState({ loading: true, error: null });
        try {
            const { startDate, endDate } = this.props;
            const res = await adminService.getFinancialsKpis({ startDate, endDate });
            if (res.statusCode === 1) {
                this.setState({ data: res.responseData, loading: false });
            } else {
                this.setState({ error: res.message || 'Failed to load KPIs', loading: false });
            }
        } catch (err) {
            console.error('financials kpis error', err);
            this.setState({ error: String(err), loading: false });
        }
    };

    tile(label, value, sub) {
        return (
            <div key={label} className="fin-kpi-tile">
                <div className="fin-kpi-tile-label">{label}</div>
                <div className="fin-kpi-tile-value">{value}</div>
                {sub && <div className="fin-kpi-tile-sub">{sub}</div>}
            </div>
        );
    }

    renderCfo(d) {
        const tiles = [
            this.tile('MRR', fmtGBP(d.mrr, { decimals: 0 }), 'Estimated (pending Stripe Billing sync)'),
            this.tile('ARR', fmtGBP(d.arr, { decimals: 0 })),
            this.tile('ARPA', fmtGBP(d.arpa, { decimals: 0 }), 'Incl. processing + invoicing'),
            this.tile('Take-rate', pct(d.takeRatePct, 2), 'Beu fee / on-platform volume'),
            this.tile('Gross margin', pct(d.grossMarginPct), 'Net ÷ gross revenue'),
            this.tile('Rule of 40', d.ruleOf40 == null ? '—' : decimal(d.ruleOf40), 'Growth % + margin %. >40 = healthy'),
            this.tile('Net New MRR', '—', 'Phase 2: needs prior-month sub snapshot'),
            this.tile('GRR', '—', 'Phase 2: sub retention'),
            this.tile('NRR', '—', 'Phase 2: sub retention + expansion')
        ];
        return <div className="fin-kpi-strip">{tiles}</div>;
    }

    renderPayments(d) {
        const tiles = [
            this.tile('GPV on-platform (MTD)', fmtGBP(d.gpvOnPlatform, { decimals: 0 })),
            this.tile('GPV off-platform (MTD)', fmtGBP(d.gpvOffPlatform, { decimals: 0 })),
            this.tile('On-platform %', pct(d.onPlatformPct), 'Target: rising month on month'),
            this.tile('GPV per active salon', fmtGBP(d.gpvPerActiveSalon, { decimals: 0 })),
            this.tile('Avg transaction size', fmtGBP(d.avgTxnSize)),
            this.tile('Txns per active salon', decimal(d.txnsPerSalon, 0), 'This month'),
            this.tile('Per-txn margin', fmtGBP(d.perTxnMargin), 'Beu fee − Stripe fee')
        ];
        return <div className="fin-kpi-strip">{tiles}</div>;
    }

    renderOps(d) {
        const rateLabel = d.activation?.rate == null ? '—' : pct(d.activation.rate);
        const rateSub = d.activation?.rate == null
            ? 'No new salons this month yet'
            : `${d.activation.activated} of ${d.activation.newSalons} new salons took ≥1 Beu payment`;
        const tiles = [
            this.tile('Active salons', fmtInt(d.activeSalons)),
            this.tile('Active hairdressers', fmtInt(d.activeHairdressers), 'Took ≥1 booking last 30 days'),
            this.tile('SE connect accounts', fmtInt(d.activeSEConnectAccounts)),
            this.tile('Activation rate', rateLabel, rateSub),
            this.tile(
                'Stuck transfers',
                fmtGBP(d.stuckTransfers?.totalStuck, { decimals: 0 }),
                `${fmtInt(d.stuckTransfers?.count)} deposits — transfer failed silently (Suzi pattern)`
            ),
            this.tile(
                'Pending payment links',
                fmtInt(d.pendingPaymentLinks?.count),
                `${fmtGBP(d.pendingPaymentLinks?.totalAmount, { decimals: 0 })} outstanding`
            ),
            this.tile('Dispute rate', '—', 'Phase 2: needs Stripe Disputes API'),
            this.tile('Failed-txn rate', '—', 'Phase 2')
        ];
        return <div className="fin-kpi-strip">{tiles}</div>;
    }

    renderGrowth(d) {
        const tiles = [
            this.tile('MoM net revenue growth', signedPct(d.momGrowthPct), `MTD ${fmtGBP(d.mtdNet)} vs prev ${fmtGBP(d.prevNet)}`),
            this.tile('Cohort retention', '—', d.cohortsNote || 'Phase 2'),
            this.tile('LTV (rough)', '—', 'Phase 2: needs churn rate'),
            this.tile('CAC payback', '—', 'Phase 2: needs CAC tracking')
        ];
        return <div className="fin-kpi-strip">{tiles}</div>;
    }

    render() {
        const { loading, error, data, audience } = this.state;
        if (loading) return <div className="fin-loading">Loading KPIs…</div>;
        if (error) return <div className="fin-error">{error}</div>;
        if (!data) return null;

        let content = null;
        switch (audience) {
            case 'payments': content = this.renderPayments(data.payments || {}); break;
            case 'ops':      content = this.renderOps(data.ops || {}); break;
            case 'growth':   content = this.renderGrowth(data.growth || {}); break;
            case 'cfo':
            default:         content = this.renderCfo(data.cfo || {}); break;
        }

        return (
            <div>
                <div className="financials-tabs" style={{ marginBottom: 20 }}>
                    {AUDIENCE_TABS.map(t => (
                        <button
                            key={t.id}
                            className={`financials-tab-btn ${audience === t.id ? 'active' : ''}`}
                            onClick={() => this.setState({ audience: t.id })}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                {content}
            </div>
        );
    }
}

export default KpisView;
